import { useCallback } from 'react';
import { Flex, Stack } from '@mantine/core';

import { Button, CalculatorForm, NumberInput, Select, Text, Textarea, TextInput, YesNoRadioButton } from 'components';
import { AppConstants } from '../../../../constants';
import { CreditRequestStep, ValidationRules } from 'types';
import { AppUtils } from 'utils';

import classes from './index.module.css';

const {
  CREDIT_AMOUNT_MAX,
  CREDIT_AMOUNT_MIN,
  CREDIT_EXISTING_MONTHLY_INSTALLMENT_MAX,
  CREDIT_EXISTING_MONTHLY_INSTALLMENT_MIN,
  CREDIT_PAYMENT_MAX,
  CREDIT_PAYMENT_MIN,
} = AppConstants;

const CreditInformationStep = ({
  form,
  isEditable,
  onSubmit,
  options,
  t,
}: CreditRequestStep) => {

  const onSubmitWrapper = useCallback(() => {
    if (isEditable && !isFormValid()) {
      return;
    }
    onSubmit();
  }, [form.values, options, isEditable]);

  const isFormValid = useCallback(() => {
    let isValid = true;

    const runValidation = (name: string, value: any, rules: ValidationRules) => {
      const result = AppUtils.validateField(name, value, rules, form, t);
      if (!result) {
        isValid = false;
      }
    };

    runValidation('creditAmount', form.values.creditAmount,
      {
        required: true,
        min: options.creditAmount?.min ?? CREDIT_AMOUNT_MIN,
        max: options.creditAmount?.max ?? CREDIT_AMOUNT_MAX,
        requiredMessage: t('input.creditAmount.error.required'),
        minMessage: t('input.creditAmount.error.min', { amount: options.creditAmount?.min ?? CREDIT_AMOUNT_MIN }),
        maxMessage: t('input.creditAmount.error.max', { amount: options.creditAmount?.max ?? CREDIT_AMOUNT_MAX }),
      }
    );

    runValidation('paymentPeriod', form.values.paymentPeriod,
      {
        required: true,
        min: options.paymentPeriod?.min ?? CREDIT_PAYMENT_MIN,
        max: options.paymentPeriod?.max ?? CREDIT_PAYMENT_MAX,
        requiredMessage: t('input.paymentPeriod.error.required'),
        minMessage: t('input.paymentPeriod.error.min', { amount: options.paymentPeriod?.min ?? CREDIT_PAYMENT_MIN }),
        maxMessage: t('input.paymentPeriod.error.max', { amount: options.paymentPeriod?.max ?? CREDIT_PAYMENT_MAX }),
      }
    );

    runValidation('creditInsurance', form.values.creditInsurance, { required: true });

    runValidation('creditType', form.values.creditType, { required: true });
    if (form.values.creditType === 'NEW') {
      runValidation('creditPurpose', form.values.creditPurpose, { required: true });

      if (form.values.creditPurpose === 'OTHER') {
        runValidation('creditPurposeNote', form.values.creditPurposeNote, { required: true });
      }
    }

    if (form.values.creditType === 'REFINANCING') {
      runValidation('existingMonthlyInstallment', form.values.existingMonthlyInstallment,
        {
          required: true,
          min: options.existingMonthlyInstallment?.min ?? CREDIT_EXISTING_MONTHLY_INSTALLMENT_MIN,
          max: options.existingMonthlyInstallment?.max ?? CREDIT_EXISTING_MONTHLY_INSTALLMENT_MAX,
          minMessage: t('input.existingMonthlyInstallment.error.min', {
            amount: options.existingMonthlyInstallment?.min ?? CREDIT_EXISTING_MONTHLY_INSTALLMENT_MIN,
          }),
          maxMessage: t('input.existingMonthlyInstallment.error.max', {
            amount: options.existingMonthlyInstallment?.max ?? CREDIT_EXISTING_MONTHLY_INSTALLMENT_MAX,
          }),
        }
      );

      runValidation('bank', form.values.bank, { required: true });

      if (form.values.bank === 'OTHER') {
        runValidation('bankName', form.values.bankName, { required: true });
      }

      runValidation('refinanceCreditCard', form.values.refinanceCreditCard, { required: true });

      runValidation('refinanceLease', form.values.refinanceLease, { required: true });
    }

    return isValid;
  }, [form.values, options]);

  return (
    <Flex direction={{ base: 'column', md: 'row' }} align={{ base: 'stretch', md: 'flex-start' }} mt={60} gap='100'>
      <CalculatorForm
        form={form}
        options={options}
        t={t}
        sectionVariant={'green'}
        labelVariant={'dark'}
        sliderColor={'#2F6F16'}
        variant={'light'}
        disabled={!isEditable}
      />
      <Stack className={classes.stack}>
        <YesNoRadioButton
          name={'creditInsurance'}
          label={t('input.creditInsurance.label')}
          t={t}
          form={form}
          required={true}
          info={t('input.creditInsurance.info.message')}
          disabled={!isEditable}
        />
        <Select
          label={t('input.creditType.label')}
          data={options['creditType'].map(el => (
            { value: el, label: t(`input.creditType.options.${el}`) }
          ))}
          placeholder={t('input.creditType.placeholder')}
          key={form.key('creditType')}
          {...form.getInputProps('creditType')}
          clearable={true}
          required={true}
          info={t('input.creditType.info.message')}
          disabled={!isEditable}
        />
        {form.values.creditType === 'NEW' ?
          <>
            <Select
              label={t('input.creditPurpose.label')}
              data={options['creditPurpose'].map(el => (
                { value: el, label: t(`input.creditPurpose.options.${el}`) }
              ))}
              placeholder={t('input.creditPurpose.placeholder')}
              key={form.key('creditPurpose')}
              {...form.getInputProps('creditPurpose')}
              clearable={true}
              required={true}
              info={t('input.creditPurpose.info.message')}
              disabled={!isEditable}
            />
            {form.values.creditPurpose === 'OTHER' ?
              <TextInput
                label={t('input.creditPurposeNote.label')}
                key={form.key('creditPurposeNote')}
                {...form.getInputProps('creditPurposeNote')}
                required={true}
                disabled={!isEditable}
              />
              : undefined}
          </>
          : undefined}
        {form.values.creditType === 'REFINANCING' ?
          <>
            <NumberInput
              label={t('input.existingMonthlyInstallment.label')}
              key={form.key('existingMonthlyInstallment')}
              {...form.getInputProps('existingMonthlyInstallment')}
              rightSection={<Text size={'sm'} >{t('currency.switzerland')}</Text>}
              rightSectionWidth={40}
              required={true}
              info={t('input.existingMonthlyInstallment.info.message')}
              disabled={!isEditable}
            />
            <Select
              label={t('input.bank.label')}
              data={options['bank'].map(el => (
                { value: el, label: t(`input.bank.options.${el}`) }
              ))}
              placeholder={t('input.bank.placeholder')}
              key={form.key('bank')}
              {...form.getInputProps('bank')}
              clearable={true}
              required={true}
              info={t('input.bank.info.message')}
              disabled={!isEditable}
            />
            {form.values.bank === 'OTHER' ?
              <TextInput
                label={t('input.bankName.label')}
                key={form.key('bankName')}
                {...form.getInputProps('bankName')}
                required={true}
                info={t('input.bankName.info.message')}
                disabled={!isEditable}
              />
              : undefined}
            <YesNoRadioButton
              name={'refinanceCreditCard'}
              label={t('input.refinanceCreditCard.label')}
              t={t}
              form={form}
              required={true}
              info={t('input.refinanceCreditCard.info.message')}
              disabled={!isEditable}
            />
            <YesNoRadioButton
              name={'refinanceLease'}
              label={t('input.refinanceLease.label')}
              t={t}
              form={form}
              required={true}
              info={t('input.refinanceLease.info.message')}
              disabled={!isEditable}
            />
          </>
          : undefined}
        <Textarea
          label={t('input.comment.label')}
          key={form.key('comment1')}
          {...form.getInputProps('comment1')}
          info={t('input.comment.info.message')}
          disabled={!isEditable}
        />
        <Button onClick={onSubmitWrapper} w='min-content' px='xl'>
          {t('action.next')}
        </Button>
      </Stack>
    </Flex>
  )
}

export default CreditInformationStep;